import { useEffect, useState, useCallback, useMemo } from 'react';

import { observer } from 'mobx-react';
import { Spinner, Table, Tag, Alert } from '@bringg/react-components';

import type { DeploymentHistoryEvent, DeploymentHistoryApiResponse } from './types';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, TABLE_SCROLL_Y, TIMESTAMP_FORMAT_OPTIONS } from './constants';
import { useDeploymentHistoryTranslations } from './use-deployment-history-translations';
import { useStores } from '../../recipes';

import styles from './deployment-history.module.scss';

const DEPLOYMENT_HISTORY_API_URL = `${window.location.origin}/apps/deployments/history.json`;

const TYPE_COLORS: Record<string, string> = {
	Backend: '#1890ff',
	Frontend: '#52c41a',
	Other: '#8c8c8c'
};

const mapDeploymentType = (apiType: string): string => {
	const typeMap: Record<string, string> = {
		service: 'Backend',
		webapp: 'Frontend'
	};
	return typeMap[apiType] || 'Other';
};

const generateChangeDescription = (productDomain: string[]): string => {
	if (!productDomain || productDomain.length === 0) {
		return 'Other update';
	}
	return `Update in ${productDomain.join(', ')} area${productDomain.length > 1 ? 's' : ''}`;
};

const DeploymentHistory = observer(() => {
	const { merchantConfigurationsStore } = useStores();
	const translations = useDeploymentHistoryTranslations();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [events, setEvents] = useState<DeploymentHistoryEvent[]>([]);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [currentPage, setCurrentPage] = useState(1);

	const loadEvents = useCallback(async () => {
		setLoading(true);
		setError(null);

		try {
			const response = await fetch(DEPLOYMENT_HISTORY_API_URL);

			if (!response.ok) {
				throw new Error(`Failed to fetch deployment history: ${response.status} ${response.statusText}`);
			}

			const data: DeploymentHistoryApiResponse = await response.json();

			// Sort by timestamp (newest first), then map to DeploymentHistoryEvent
			const formattedEvents: DeploymentHistoryEvent[] = [...data]
				.sort((a, b) => (b.timestamp > a.timestamp ? -1 : 1))
				.map((item, index) => ({
					rowKey: `${new Date(item.timestamp).getTime()}-${index}`,
					timestamp: new Date(item.timestamp),
					change: generateChangeDescription(item.product_domain),
					type: mapDeploymentType(item.type)
				}));
			setEvents(formattedEvents);
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : translations.errorMessage;
			setError(errorMessage);
			setEvents([]);
		} finally {
			setLoading(false);
		}
	}, [translations.errorMessage]);

	useEffect(() => {
		void loadEvents();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Fetch only on mount

	// Get unique types from events for dynamic filters
	const uniqueTypes = useMemo(() => {
		const seen = new Set<string>();
		const result: Array<{ text: string; value: string }> = [];
		events.forEach(event => {
			if (!seen.has(event.type)) {
				seen.add(event.type);
				result.push({ text: event.type, value: event.type });
			}
		});
		return result;
	}, [events]);

	const locale = merchantConfigurationsStore.defaultLanguageCode || 'en-US';

	const columns = useMemo(
		() => [
			{
				title: translations.columns.timestamp,
				dataIndex: 'timestamp',
				key: 'timestamp',
				width: 200,
				render: (timestamp: Date) => timestamp.toLocaleString(locale, TIMESTAMP_FORMAT_OPTIONS),
				sorter: (a: DeploymentHistoryEvent, b: DeploymentHistoryEvent) =>
					a.timestamp.getTime() - b.timestamp.getTime(),
				defaultSortOrder: 'descend' as const
			},
			{
				title: translations.columns.change,
				dataIndex: 'change',
				key: 'change',
				ellipsis: true
			},
			{
				title: translations.columns.type,
				dataIndex: 'type',
				key: 'type',
				width: 150,
				render: (type: string) => <Tag color={TYPE_COLORS[type]}>{type}</Tag>,
				filters: uniqueTypes,
				onFilter: (value: string | number | boolean, record: DeploymentHistoryEvent) => record.type === value
			}
		],
		[translations, uniqueTypes, locale]
	);

	if (loading) {
		return (
			<div className={styles.deploymentHistoryLoading}>
				<Spinner size="large" />
				<p className={styles.deploymentHistoryLoadingText}>{translations.loading}</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className={styles.deploymentHistory}>
				<div className={styles.deploymentHistoryTitle}>
					<h2>{translations.title}</h2>
				</div>
				<Alert
					message={translations.errorTitle}
					description={error}
					type="error"
					className={styles.deploymentHistoryError}
					showIcon
				/>
			</div>
		);
	}

	return (
		<div className={styles.deploymentHistory}>
			<div className={styles.deploymentHistoryTitle}>
				<h2>{translations.title}</h2>
			</div>

			<Table
				className={styles.deploymentHistoryTable}
				columns={columns}
				dataSource={events}
				rowKey="rowKey"
				pagination={{
					current: currentPage,
					pageSize: pageSize,
					total: events.length,
					showSizeChanger: true,
					showQuickJumper: true,
					showTotal: (total, range) =>
						`${range[0]}-${range[1]} of ${total} ${translations.pagination.events}`,
					pageSizeOptions: PAGE_SIZE_OPTIONS,
					onShowSizeChange: (current, size) => {
						setPageSize(size);
						setCurrentPage(1);
					},
					onChange: page => {
						setCurrentPage(page);
					}
				}}
				scroll={{ y: TABLE_SCROLL_Y }}
				size="small"
			/>
		</div>
	);
});

export default DeploymentHistory;
